import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiRotateCcw,
} from "react-icons/fi";
import { useSeo } from "./useHook";
import SeoModal from "./components/SeoModal";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import Loader from "../../../Utils/loader";

function SeoManagement() {
  const { getModules, getSeoList, deleteSeo } = useSeo();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.SeoManagement.CREATE);
  const canUpdate = hasPermission(SystemPermissions.SeoManagement.UPDATE);
  const canDelete = hasPermission(SystemPermissions.SeoManagement.DELETE);
  const showActions = canUpdate || canDelete;

  const [modules, setModules] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(20);

  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    filterType: "",
    search: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const groupedModules = useMemo(() => {
    const groups = {};
    modules.forEach((m) => {
      if (!groups[m.group]) groups[m.group] = [];
      groups[m.group].push(m);
    });
    return groups;
  }, [modules]);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getSeoList({
        entityType: appliedFilters.filterType || undefined,
        search: appliedFilters.search || undefined,
        offset,
        limit,
      });
      setItems(data.items || []);
      setTotal(data.pagination?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching SEO list:", error);
      setItems([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    appliedFilters.filterType,
    appliedFilters.search,
    offset,
    limit,
    getSeoList,
  ]);

  useEffect(() => {
    getModules().then(setModules).catch(() => setModules([]));
  }, [getModules]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setOffset(0);
    setAppliedFilters({
      filterType,
      search: search.trim(),
    });
  };

  const handleReset = () => {
    setFilterType("");
    setSearch("");
    setOffset(0);
    setAppliedFilters({
      filterType: "",
      search: "",
    });
  };

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const openCreate = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const openEdit = (row) => {
    setEditData(row);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Delete SEO?",
      text: `Remove SEO for ${row.entity_label || row.entity_id}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteSeo(row.id);
      Swal.fire({
        title: "Deleted",
        text: "SEO entry removed.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
      await fetchList();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to delete", "error");
    }
  };

  if (isLoading && items.length === 0) {
    return <Loader message="Loading SEO entries..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Dynamic Page</h2>
        {canCreate && (
          <button
            type="button"
            onClick={openCreate}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add SEO
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <select
          className="status-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">All modules</option>
          {Object.entries(groupedModules).map(([group, opts]) => (
            <optgroup key={group} label={group}>
              {opts.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search title, slug, keywords..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <FiSearch className="search-icon" />
        </div>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button
            type="button"
            className="btn btn-filter filter-btn"
            onClick={handleSearch}
          >
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            type="button"
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Module</th>
                <th className="table-th">Item</th>
                <th className="table-th">Meta Title</th>
                <th className="table-th">Slug</th>
                <th className="table-th">Robots</th>
                {showActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={showActions ? 6 : 5}
                    className="text-center py-10 text-gray-500 text-lg font-medium"
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                items.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      <div className="font-medium">{row.module_label}</div>
                      <div className="text-xs text-gray-500">
                        {row.module_group}
                      </div>
                    </td>
                    <td className="table-td">
                      {row.entity_label || row.entity_id}
                    </td>
                    <td className="table-td max-w-[220px] truncate">
                      {row.meta_title || "—"}
                    </td>
                    <td className="table-td font-mono text-xs text-blue-600">
                      {row.slug}
                    </td>
                    <td className="table-td">{row.robots || "—"}</td>
                    {showActions && (
                      <td className="table-td">
                        <div className="flex flex-wrap gap-2">
                          {canUpdate && (
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="btn btn-edit text-white"
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              className="btn btn-delete text-white"
                            >
                              <FiTrash2 className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      <SeoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editData={editData}
        groupedModules={groupedModules}
        onSuccess={fetchList}
      />
    </div>
  );
}

export default SeoManagement;
